'use client';

import type { CerFeatures } from '@slims/shared/data';

import { useMemo } from 'react';
import { type ScopeLeaf, useScopeTree } from '@/hooks/use-scope-tree';
import { useScopeStore } from '@/stores/scope-store';

interface UseSelectedScopesReturn {
	/** The resolved selected configuration leaves. */
	scopes: ScopeLeaf[];
	/** True when more than one scope is selected (comparison mode). */
	isMulti: boolean;
	loading: boolean;
}

export function useSelectedScopes(feature?: keyof CerFeatures): UseSelectedScopesReturn {
	const { leaves, loading } = useScopeTree();
	const selectedOrgIds = useScopeStore((s) => s.selectedOrgIds);

	// Memoizzato sui due riferimenti di store (entrambi stabili finché non
	// cambiano davvero): chi ci monta sopra una griglia — la Dashboard — riceve
	// lo stesso array a ogni render, e non rimonta le card a ogni giro.
	const scopes = useMemo(() => {
		const byId = new Map(leaves.map((l) => [l.orgId, l]));
		return selectedOrgIds
			.map((id) => byId.get(id))
			.filter(
				(s): s is ScopeLeaf =>
					Boolean(s) &&
					(!feature ||
						(s?.features?.[feature] !== undefined &&
							s.features[feature] !== false &&
							s.features[feature] !== 'disabled')),
			);
	}, [leaves, selectedOrgIds, feature]);

	return { scopes, isMulti: scopes.length > 1, loading };
}
